<!--
  The notifications dropdown: the latest ten, "View all" and "Mark all as
  read". The bell in the top bar and the bell in the sidebar both open it.
  An open team invitation is the first row and stays until the member
  answers it: it is not a feed item, so "Mark all as read" leaves it.
-->
<template>
  <div class="w-96 overflow-hidden rounded-lg shadow-md">
    <FocusLoop
      :is-active="isFocusLoopActive"
      :auto-focus="false"
      is-arrows-navigation-active
    >
      <div>
        <!-- The padding is on the wrapper, like the list below, so the
             focus ring of the row is not cut by the panel edge. -->
        <div
          v-if="invite"
          class="p-2"
          :class="{
            'border-b border-neutral-200':
              notificationsStore.notifications.length,
          }"
        >
          <InertiaLink href="/team/invitations" class="block rounded-md">
            <div
              class="flex items-center rounded-md p-2 transition-colors hover:bg-neutral-50"
            >
              <div class="flex items-center">
                <div class="bg-primary-500 mr-3 h-2 w-2 rounded-full" />
                <UsersIcon class="text-secondary-500 mr-4 h-4 w-4" />
              </div>
              <div class="flex grow flex-col">
                <div class="text-sm">
                  <template v-if="invite.teamName">
                    <strong>{{ invite.teamName }}</strong>
                    invited you to their team.
                  </template>
                  <template v-else>You are invited to a team.</template>
                </div>
                <div class="text-xs text-neutral-400">See the invitation</div>
              </div>
            </div>
          </InertiaLink>
        </div>
        <div
          v-if="notificationsStore.notifications.length"
          class="max-h-72 divide-y divide-neutral-200 overflow-y-auto p-2"
        >
          <div
            v-for="notification in notificationsPreview"
            :key="notification.id"
          >
            <Notification :notification="notification" is-in-dropdown />
          </div>
        </div>
        <div
          v-else-if="!invite"
          class="p-4 text-center text-xs text-neutral-400"
        >
          You have no notifications.
        </div>
        <div
          class="grid place-items-center items-center border-t border-t-neutral-200 bg-white text-sm font-medium"
          :class="{
            'grid-cols-2': notificationsStore.unreadCount !== 0,
          }"
        >
          <InertiaLink
            href="/notifications"
            class="text-primary-500 hover:bg-primary-50 w-full p-2 text-center transition-colors"
          >
            View all
          </InertiaLink>
          <button
            v-if="notificationsStore.unreadCount !== 0"
            class="w-full p-2 text-center text-neutral-500 hover:bg-neutral-50"
            @click="handleMarkAllNotificationsAsRead"
          >
            Mark all as read
          </button>
        </div>
      </div>
    </FocusLoop>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { notify } from '@kyvg/vue3-notification'
import UsersIcon from '~icons/lucide/users'
import FocusLoop from '@/shared/components/FocusLoop.vue'
import Notification from '@/modules/notifications/components/Notification.vue'
import { useNotificationsStore } from '@/shared/stores/notifications.js'
import { useShellNotices } from '@/shared/components/sidebar/useShellNotices'

defineProps<{ isFocusLoopActive: boolean }>()

// A GetStream notification group. The store is plain JavaScript, so the
// type names the one field this list reads.
type NotificationGroup = { id: string }

const notificationsStore = useNotificationsStore()
const { teamInvite: invite } = useShellNotices()

const notificationsPreview = computed(() =>
  (notificationsStore.notifications as NotificationGroup[]).slice(0, 10),
)

const handleMarkAllNotificationsAsRead = () => {
  notificationsStore.markAllNotificationsAsRead()
  notify({
    text: 'All notifications marked as read.',
    type: 'success',
  })
}
</script>
